/**
 * P2.2 — Bubble clusterer.
 *
 * Groups sibling bubbles into super-bubbles when the overview would
 * otherwise render too many cards to scan. Pure function; safe to
 * call from the processing worker as well as the browser.
 *
 * Algorithm (deterministic):
 *  1. If `bubbles.length <= threshold`, pass through unchanged.
 *  2. Otherwise keep the top `keepLargest` bubbles (by resource count)
 *     intact so the headline workloads stay visible.
 *  3. Partition the remaining bubbles by a group key — `kind` alone by
 *     default, or caller-supplied (`keyOf`) for env / team grouping.
 *  4. For each partition whose size is ≥ `minClusterSize`, emit one
 *     synthetic cluster bubble with merged counts / tier breakdowns /
 *     interior dots and carry its member ids. Small partitions pass
 *     through.
 *
 * Super-bubbles reuse the existing `CompassContainerCard` renderer —
 * they carry the same `BubbleNode` shape plus `attrs.cluster: true`
 * and `attrs.memberIds` on the scene side (stamped by callers).
 */

import type { BubbleNode, BubbleInteriorDot } from './focusFilter';

export interface ClustererOptions {
  /** Pass-through when total bubble count ≤ this value. */
  readonly threshold: number;
  /** Keep this many largest bubbles intact before clustering the tail. */
  readonly keepLargest: number;
  /** A partition smaller than this stays unclustered. */
  readonly minClusterSize: number;
  /** Custom group key — defaults to bubble kind (account / vpc / subnet). */
  readonly keyOf?: (bubble: BubbleNode) => string;
}

export const DEFAULT_CLUSTERER_OPTIONS: ClustererOptions = {
  threshold: 30,
  keepLargest: 12,
  minClusterSize: 3,
};

export interface BubbleClusterMembership {
  /** Synthetic bubble id emitted for the cluster. */
  readonly clusterId: string;
  /** Original member bubble ids absorbed by the cluster. */
  readonly memberIds: readonly string[];
}

export interface BubbleClustererResult {
  /** Visible bubbles after clustering (top-N + clusters + stragglers). */
  readonly bubbles: BubbleNode[];
  /** Per-cluster membership; empty when no clustering ran. */
  readonly clusters: readonly BubbleClusterMembership[];
  /** True iff clustering changed the bubble list. */
  readonly didCluster: boolean;
}

export function clusterBubbles(
  input: readonly BubbleNode[],
  options: Partial<ClustererOptions> = {},
): BubbleClustererResult {
  const opts: ClustererOptions = { ...DEFAULT_CLUSTERER_OPTIONS, ...options };
  const keyOf = opts.keyOf ?? ((b) => b.kind);

  if (input.length <= opts.threshold) {
    return { bubbles: [...input], clusters: [], didCluster: false };
  }

  // Biggest bubbles stay as-is.
  const sorted = [...input].sort((a, b) => b.resourceCount - a.resourceCount);
  const kept = sorted.slice(0, opts.keepLargest);
  const tail = sorted.slice(opts.keepLargest);

  // Partition the tail.
  const partitions = new Map<string, BubbleNode[]>();
  for (const b of tail) {
    const k = keyOf(b);
    const arr = partitions.get(k) ?? [];
    arr.push(b);
    partitions.set(k, arr);
  }

  const bubbles: BubbleNode[] = [...kept];
  const clusters: BubbleClusterMembership[] = [];

  // Deterministic partition order for stable scene output.
  const partitionKeys = Array.from(partitions.keys()).sort();
  for (const key of partitionKeys) {
    const members = partitions.get(key)!;
    if (members.length < opts.minClusterSize) {
      bubbles.push(...members);
      continue;
    }
    const cluster = mergeBubbles(members, key);
    bubbles.push(cluster);
    clusters.push({
      clusterId: cluster.id,
      memberIds: members.map((m) => m.id),
    });
  }

  return { bubbles, clusters, didCluster: clusters.length > 0 };
}

function mergeBubbles(members: readonly BubbleNode[], key: string): BubbleNode {
  const totalCount = members.reduce((acc, m) => acc + m.resourceCount, 0);
  const tierBreakdown: Record<string, number> = {};
  const interior: BubbleInteriorDot[] = [];
  const memberIds: string[] = [];
  let monthlyCostUsd = 0;
  let costCarrierCount = 0;
  let costCarrierMemberCount = 0;
  let utilizationWeightedTotal = 0;
  let utilizationMemberCount = 0;
  let wastedMonthlyUsd = 0;
  for (const m of members) {
    for (const [tier, n] of Object.entries(m.tierBreakdown)) {
      tierBreakdown[tier] = (tierBreakdown[tier] ?? 0) + n;
    }
    if (m.interior) {
      for (const dot of m.interior) interior.push(dot);
    }
    if (m.memberIds) {
      memberIds.push(...m.memberIds);
    } else if (m.interior) {
      memberIds.push(...m.interior.map((dot) => dot.id));
    } else {
      memberIds.push(m.referenceId);
    }
    if (m.monthlyCostUsd !== undefined) {
      monthlyCostUsd += m.monthlyCostUsd;
      // `costCoverage` is a fraction of the member's own resources;
      // multiply by the member's resource count to recover the raw
      // cost-carrier count so the cluster-level coverage stays sound.
      const coverage = m.costCoverage ?? 0;
      costCarrierCount += Math.round(coverage * m.resourceCount);
      costCarrierMemberCount += m.resourceCount;
    }
    if (m.utilizationPct !== undefined) {
      utilizationWeightedTotal += m.utilizationPct * m.resourceCount;
      utilizationMemberCount += m.resourceCount;
    }
    if (m.wastedMonthlyUsd !== undefined) {
      wastedMonthlyUsd += m.wastedMonthlyUsd;
    }
  }
  const representative = members[0];
  // Cap interior dots — the card can't render tens of thousands of
  // Konva circles cheaply. 200 matches the renderer's dot budget.
  const MAX_INTERIOR_DOTS = 200;
  const trimmedInterior = interior.slice(0, MAX_INTERIOR_DOTS);

  return {
    id: `bubble-cluster:${key}:${members.length}`,
    type: 'compass_bubble',
    label: `${capitalize(key)} cluster · ${members.length} items`,
    kind: representative.kind,
    referenceId: `cluster:${key}`,
    memberIds,
    groupKind: representative.groupKind,
    resourceCount: totalCount,
    tierBreakdown,
    ...(costCarrierCount > 0
      ? {
          monthlyCostUsd,
          costCoverage:
            costCarrierMemberCount > 0 ? costCarrierCount / costCarrierMemberCount : 0,
        }
      : {}),
    ...(utilizationMemberCount > 0
      ? {
          utilizationPct: utilizationWeightedTotal / utilizationMemberCount,
          ...(wastedMonthlyUsd > 0 ? { wastedMonthlyUsd } : {}),
        }
      : {}),
    interior: trimmedInterior,
  };
}

function capitalize(s: string): string {
  return s.length === 0 ? s : s[0].toUpperCase() + s.slice(1);
}
